import { EditorTree } from '../../../types/types';

/**
 * Mock implementation of the Computation service for local development and testing
 */
class MockComputationService {
  private simulateDelay: () => Promise<void>;

  constructor(api_delay: number) {
    this.simulateDelay = (): Promise<void> => {
      return new Promise((resolve) => {
        setTimeout(resolve, api_delay);
      });
    };
  }

  /**
   * Calculate tree values on the backend
   */
  async calculateTree(tree: EditorTree): Promise<EditorTree> {
    await this.simulateDelay();
    
    // Simple calculation logic that could be similar to the local calculation
    // In a real backend this would do more sophisticated calculations
    
    console.log('Tree calculation requested from backend');
    
    // Here we just return the tree as-is with an updated timestamp
    // In reality, the backend would calculate expected values
    return {
      ...tree,
      updatedAt: new Date().toISOString()
    };
  }

  /**
   * Run sensitivity analysis for a tree
   */
  async runSensitivityAnalysis(treeId: string, params: any): Promise<any> {
    await this.simulateDelay();

    console.log(`Sensitivity analysis requested for tree ${treeId} with params:`, params);
    
    // Mock response with sensitivity analysis results
    return {
      results: {
        sensitivity_data: [
          { value: 0.1, result: 100000 },
          { value: 0.2, result: 120000 },
          { value: 0.3, result: 140000 },
          { value: 0.4, result: 160000 },
          { value: 0.5, result: 180000 },
        ],
        parameter: params.node_name,
        type: params.param_type
      }
    };
  }
  
  /**
   * Run Monte Carlo simulation for a tree
   */
  async runSimulation(treeId: string, settings: any): Promise<any> {
    await this.simulateDelay();

    console.log(`Simulation requested for tree ${treeId} with settings:`, settings);
    
    // Mock response with simulation results
    return {
      results: {
        stats: {
          option1: {
            mean: 150000,
            median: 140000,
            std: 50000,
            min: 50000,
            max: 300000,
            q25: 120000,
            q75: 180000
          },
          option2: {
            mean: 100000,
            median: 90000,
            std: 40000,
            min: 20000,
            max: 200000,
            q25: 70000,
            q75: 130000
          }
        },
        win_probability: {
          option1: { vs_option2: 0.65 },
          option2: { vs_option1: 0.35 }
        }
      }
    };
  }

  async generateAISummary(params: any) {
        await this.simulateDelay();

        return {
            id: `ai_summary_${Date.now()}`,
            type: params,
            data: {
                summary: `The core decision revolves around selecting between two distinct product development paths: \"Product 1\" and \"Product 2\". The objective is to maximize the expected financial return.\n\n**Key Decision Points and Choices:**\nThe initial and sole decision point is to choose between investing in \"Product 1\" or \"Product 2\".\n\n**Chance Events and Their Impact:**\nFor either product choice, there are three subsequent layers of uncertainty that significantly impact the final outcome:\n\n1.  **Startup Capital:**\n    *   For \"Product 1\", there's a higher likelihood (70%) of requiring \"Low\" initial capital, and a lower chance (30%) of \"High\" capital.\n    *   Conversely, for \"Product 2\", there's a higher likelihood (70%) of needing \"High\" initial capital, and a lower chance (30%) of \"Low\" capital. This suggests \"Product 2\" is generally more capital-intensive upfront.\n\n2.  **Cost of Goods Sold (CGS):**\n    *   For \"Product 1\", regardless of the initial capital, there's a 60% chance of \"Low\" CGS and a 40% chance of \"High\" CGS.\n    *   For \"Product 2\", the probabilities are reversed: a 60% chance of \"High\" CGS and a 40% chance of \"Low\" CGS. This indicates \"Product 2\" is also likely to have higher ongoing production costs.\n\n3.  **Revenue:**\n    *   Following the CGS outcome, both products face a final layer of uncertainty regarding market performance, leading to either \"High\" or \"Low\" revenue. In all scenarios, there's a 60% chance of \"High\" revenue and a 40% chance of \"Low\" revenue.\n    *   Critically, the magnitude of revenue differs significantly: \"Product 1\" has potential revenues of $3,000,000 (High) or $1,000,000 (Low), while \"Product 2\" offers substantially higher potential: $6,000,000 (High) or $800,000 (Low).\n\n**Which Paths Lead to Favorable or Unfavorable Results?**\n*   The analysis reveals that pursuing **\"Product 2\"** leads to the most favorable overall expected financial outcome, with an expected value of **$1,040,000**.\n*   In contrast, choosing \"Product 1\" yields a lower expected value of $660,000.\n*   Generally, paths that lead to the \"High\" revenue outcomes are most favorable, assuming the preceding costs do not disproportionately erode profitability.\n\n**Strategies or Trade-offs That Emerge:**\nThe key trade-off lies in **risk and reward versus cost structure**.\n*   **\"Product 1\"** appears to be a more conservative option, characterized by a higher probability of lower initial capital and lower ongoing costs. However, its maximum revenue potential is also considerably lower.\n*   **\"Product 2\"** is a more aggressive option. It is more likely to incur higher initial capital and higher production costs. Despite these higher cost probabilities and a slightly lower 'worst-case' revenue, its significantly higher 'best-case' revenue potential (double that of Product 1) ultimately makes it the superior choice for maximizing expected returns. The strategy derived is to embrace the higher cost likelihoods of \"Product 2\" due to its overwhelming upside potential.\n\n**Critical Observations, Patterns, or Anomalies:**\n*   **Asymmetrical Risk/Reward Profile:** \"Product 2\" presents a higher ceiling for profitability ($6,000,000) but also a lower floor ($800,000) compared to \"Product 1\" ($3,000,000 and $1,000,000 respectively). The decision to maximize expected value heavily favors this higher upside potential.\n*   **Inverse Cost Probabilities:** The probabilities of \"High\" vs. \"Low\" outcomes for Startup Capital and CGS are notably inverse between the two products. \"Product 1\" leans towards lower costs, while \"Product 2\" leans towards higher costs.\n*   **Consistent Revenue Probabilities:** Interestingly, the revenue probabilities (60% High, 40% Low) are consistent across all paths, implying that market demand for either product, once launched and produced, faces similar fundamental uncertainty regardless of its initial setup or cost efficiency. The differentiation lies primarily in the cost structures and the absolute scale of potential revenue.`,
            },
            createdAt: new Date().toISOString(),
            ok: true,
        };
    }

  /**
   * Generate the influence diagram information for a tree
   */
  async generateInfluenceDiagram(treeId: string): Promise<any> {
    await this.simulateDelay();

    console.log(`Influence diagram requested for tree ${treeId}`);
    
    // Mock response with influence diagram data
    return {
      "uncertainty_labels": [
          "CGS",
          "Revenue",
          "Startup Capital"
      ],
      "dependencies": {
          "Startup Capital": [
              "Product"
          ],
          "CGS": [
              "Product"
          ],
          "Revenue": [
              "Product"
          ]
      },
      "conditional_forms": [
          "CGS | Product=Product 1",
          "CGS | Product=Product 2",
          "Revenue | Product=Product 1",
          "Revenue | Product=Product 2",
          "Startup Capital | Product=Product 1",
          "Startup Capital | Product=Product 2"
      ],
      "conditional_form_node_ids": {
          "Startup Capital | Product=Product 1": [
              "29"
          ],
          "Startup Capital | Product=Product 2": [
              "30"
          ],
          "CGS | Product=Product 1": [
              "25",
              "26"
          ],
          "CGS | Product=Product 2": [
              "27",
              "28"
          ],
          "Revenue | Product=Product 1": [
              "17",
              "18",
              "19",
              "20"
          ],
          "Revenue | Product=Product 2": [
              "21",
              "22",
              "23",
              "24"
          ]
      }
    };
  }
}

export default MockComputationService;
